import { inject, Injectable } from '@angular/core';
import { first } from 'lodash-es';
import { firstValueFrom } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import {
  ContactType,
  CreateSiteInput,
  EntityType,
  EventType,
  GqlService,
  UpdateContactInput,
  UpdateSiteInput,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { FormValuesQuery } from '@shared/store/form-values/form-values.query';
import { MainQuery } from '@shared/store/main/main.query';

import { SiteModel, SitesStore } from './sites.store';

@Injectable({ providedIn: 'root' })
export class SitesService {
  private eventService = inject(EventService);
  private formValuesQuery = inject(FormValuesQuery);
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private sitesStore = inject(SitesStore);

  // eslint-disable-next-line max-lines-per-function
  async add({
    address_line_1,
    address_line_2,
    address_line_3,
    city,
    closeout_date,
    country,
    currency,
    investigator,
    managed_by_id,
    name,
    site_activation,
    site_no,
    state,
    target_patients,
    zip,
  }: Omit<CreateSiteInput, 'contact' | 'trial_id'> & {
    investigator: { family_name: string; given_name: string };
  }) {
    this.sitesStore.setLoading(true);
    const contact = {
      ...investigator,
      contact_type: ContactType.CONTACT_INVESTIGATOR,
      entity_type: EntityType.SITE,
    };
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createSite$(
        {
          address_line_1,
          address_line_2,
          address_line_3,
          city,
          closeout_date,
          contact,
          country,
          currency,
          managed_by_id,
          name,
          site_activation,
          site_no,
          state,
          target_patients,
          zip,
        },
        ContactType.CONTACT_INVESTIGATOR
      )
    );

    if (success && data) {
      this.sitesStore.add({
        __typename: 'Site',
        address_line_1,
        address_line_2,
        address_line_3,
        city,
        closeout_date,
        country,
        currency,
        id: data.id,
        investigator: {
          __typename: 'Contact',
          family_name: investigator.family_name,
          given_name: investigator.given_name,
          id: data.contacts ? data.contacts[0].id : '',
        },
        managed_by_id,
        name,
        site_activation,
        site_no,
        state,
        target_patients,
        zip,
      });
      this.sitesStore.setLoading(false);

      return data.id;
    }

    this.overlayService.error(errors);
    this.sitesStore.setLoading(false);

    return false;
  }

  get(includeInactive = false) {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.sitesStore.setLoading(true);

        return this.gqlService.listSites$(ContactType.CONTACT_INVESTIGATOR, includeInactive).pipe(
          tap((x) => {
            if (x.data) {
              this.sitesStore.set(
                x.data.map((site) => ({
                  ...site,
                  investigator: first(site.contacts),
                  managed_by_id: site?.managed_by?.id,
                })) as unknown as SiteModel[]
              );
            }

            this.sitesStore.setLoading(false);
          })
        );
      })
    );
  }

  getContacts(entity_id: string) {
    return this.gqlService.listSiteContacts$(entity_id, ContactType.CONTACT_INVESTIGATOR).pipe(
      map((x) => {
        return { ...x, data: x.data ? x.data[0] : null };
      })
    );
  }

  getForSitesPage() {
    this.sitesStore.setLoading(true);

    return this.gqlService.listSitesForSitesPage$(ContactType.CONTACT_INVESTIGATOR, false).pipe(
      tap((x) => {
        if (x.data) {
          this.sitesStore.set(
            x.data.map((site) => ({
              ...site,
              investigator: first(site.contacts),
              managed_by_id: site?.managed_by?.id,
            })) as unknown as SiteModel[]
          );
        }

        this.sitesStore.setLoading(false);
      })
    );
  }

  listSiteNames(includeInactive = false) {
    return this.gqlService.listSiteNames$(includeInactive).pipe(
      map((x) => {
        return { ...x, data: x.data ? x.data : null };
      })
    );
  }

  listSitePatientTrackerVersions() {
    return this.gqlService.listSitePatientTrackerVersions$().pipe(
      map((x) => {
        return { ...x, data: x.data ? x.data : null };
      })
    );
  }

  async remove(site: SiteModel) {
    const { errors, success } = await firstValueFrom(this.gqlService.removeSite$(site.id));

    if (success) {
      this.overlayService.success(`${site.name} successfully removed!`);
      this.sitesStore.remove(site.id);
      this.formValuesQuery.removeSiteFromPatientBudget(site.id);
    } else {
      this.overlayService.error(errors);
    }

    return { errors, success };
  }

  async update(
    id: string,
    site: UpdateSiteInput & { name?: string; site_no?: string },
    investigator: UpdateContactInput,
    showSuccess = true,
    previous_managed_by_id?: string
  ) {
    const { errors, success } = await firstValueFrom(
      this.gqlService.updateSite$({ ...site, contact: investigator })
    );

    if (success) {
      this.sitesStore.update(id, () => {
        return {
          ...site,
          investigator: {
            __typename: 'Contact',
            ...investigator,
          },
        };
      });

      let payload = {};

      if (previous_managed_by_id && previous_managed_by_id !== site.managed_by_id) {
        payload = { previous_managed_by_id };
      }

      await firstValueFrom(
        this.eventService.processEvent$({
          entity_id: id,
          entity_type: EntityType.SITE,
          payload: JSON.stringify(payload),
          type: EventType.UPDATE_INVESTIGATOR_TRANSACTIONS,
        })
      );

      if (showSuccess) {
        this.overlayService.success(`${site.name} successfully updated!`);
      }
    } else {
      this.overlayService.error(errors);
    }

    return { errors, success };
  }

  updateContact(entity_id: string, family_name: string, given_name: string) {
    const updateSiteContactInput = {
      family_name,
      given_name,
      id: entity_id,
    } as UpdateContactInput;

    return this.gqlService.updateContact$(updateSiteContactInput).pipe(
      map((x) => {
        return { ...x, data: x.data ? x.data : null };
      })
    );
  }
}
